import { Box, Group, Paper, SimpleGrid, Skeleton, Stack, Text, Title } from '@mantine/core'
import {
  IconChevronsRight,
  IconDisc,
  IconFolder,
  IconMusic,
  IconUser,
  IconUsers,
} from '@tabler/icons-react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useAlbums, useLibraryStats } from '~/api/queries'
import type { Album, LibraryStats } from '~/api/types'
import { AlbumCard } from '~/components/blocks/AlbumGrid/AlbumGrid'
import { SkeletonList } from '~/components/primitives/SkeletonList'

export const Route = createFileRoute('/_app/')({
  component: HomePage,
})

const statItems: { key: keyof LibraryStats; label: string; icon: typeof IconFolder }[] = [
  { key: 'folders', label: 'Folders', icon: IconFolder },
  { key: 'artists', label: 'Artists', icon: IconUser },
  { key: 'albumArtists', label: 'Album Artists', icon: IconUsers },
  { key: 'albums', label: 'Albums', icon: IconDisc },
  { key: 'tracks', label: 'Tracks', icon: IconMusic },
]

const ALBUM_GRID_COLS = { base: 2, sm: 3, md: 4, lg: 6 }

function AlbumSection({
  title,
  albums,
  isLoading,
  to,
}: {
  title: string
  albums: Album[] | undefined
  isLoading: boolean
  to?: string
}) {
  return (
    <section>
      <Title order={2} mb="md">
        {to ? (
          <Link
            to={to}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              maxWidth: '100%',
              gap: 4,
            }}
          >
            {title}
            <IconChevronsRight size={20} />
          </Link>
        ) : (
          title
        )}
      </Title>
      {isLoading ? (
        <SimpleGrid cols={ALBUM_GRID_COLS}>
          <SkeletonList count={12} height={200} />
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={ALBUM_GRID_COLS}>
          {albums?.map((album) => (
            <AlbumCard key={album.id} album={album} />
          ))}
        </SimpleGrid>
      )}
    </section>
  )
}

function HomePage() {
  const { data: stats, isLoading: statsLoading } = useLibraryStats()
  const recentAlbums = useAlbums({ sort: 'newest', limit: 12 })
  const frequentAlbums = useAlbums({ sort: 'frequent', limit: 12 })

  return (
    <Stack gap="xl">
      <Box visibleFrom="sm">
        <Title order={2} mb="md">
          Stats
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
          {statItems.map((item) => (
            <Paper key={item.key} withBorder p="md" radius="md">
              <Group gap="sm" wrap="nowrap">
                <item.icon size={20} stroke={1.5} color="var(--app-accent-fill)" />
                <div>
                  {statsLoading ? (
                    <Skeleton h={24} w={40} />
                  ) : (
                    <Text
                      fw={700}
                      size="xl"
                      lh={1}
                      style={{ color: 'var(--app-accent-secondary)' }}
                    >
                      {stats?.[item.key]?.toLocaleString() ?? 0}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed" mt={4}>
                    {item.label}
                  </Text>
                </div>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Box>

      <AlbumSection
        title="Recently Added"
        albums={recentAlbums.data?.data}
        isLoading={recentAlbums.isLoading}
        to="/recently-added"
      />
      <AlbumSection
        title="Most Played"
        albums={frequentAlbums.data?.data}
        isLoading={frequentAlbums.isLoading}
        to="/most-played"
      />
    </Stack>
  )
}
